import { PageSizePreset } from 'constants/paper';
import { PageSize } from 'types/infographTypes';
import { PageSizeUnit, PaperType } from 'types/paper.types';

import {
  HD_EXPORT_BREAKPOINT,
  LONG_PAPER_RATIO,
  MAX_NUMBER_INPUT,
  MIN_NUMBER_INPUT,
  Orientation,
  PIXEL_TO_CM_RATIO,
  PIXEL_TO_INCHES_RATIO,
} from './config';

/**
 * Precisely round a giving numeric value to specific number of deciaml place
 *
 * @param value - numeric value
 * @param decimalPlaces - round to number of decimal places
 * @returns
 */
const roundNumber = (value: number, decimalPlaces = 2) => {
  const division = Math.pow(10, decimalPlaces);
  return Math.round((value + Number.EPSILON) * division) / division;
};

/**
 * Convert pixel value to inch
 *
 * @param value - numeric value
 * @returns
 */
const pixelToInch = (value: number): number => roundNumber(value / PIXEL_TO_INCHES_RATIO);

/**
 * Convert pixel value to cm
 *
 * @param value - numeric value
 * @returns
 */
const pixelToCm = (value: number): number => roundNumber(value / PIXEL_TO_CM_RATIO);

/**
 * Convert inch value to pixel
 *
 * @param value - numeric value
 * @returns
 */
const inchToPixel = (value: number): number => Math.round(roundNumber(value * PIXEL_TO_INCHES_RATIO));

/**
 * Convert cm value to pixel
 *
 * @param value - numeric vlaue
 * @returns
 */
const cmToPixel = (value: number): number => Math.round(roundNumber(value * PIXEL_TO_CM_RATIO));

/**
 * Convert giving value to pixel
 *
 * @param value - numeric value
 * @param unit - giving value's unit in [px, in, cm]
 * @returns
 */
const toPixels = (value: number, unit = PageSizeUnit.px) => {
  switch (unit) {
    case PageSizeUnit.cm:
      return cmToPixel(value);
    case PageSizeUnit.in:
      return inchToPixel(value);
    default:
      break;
  }

  return value;
};

/**
 * Match preset paper size base on giving width and height
 *
 * @param pageSize
 * @returns  The paper type if size matches
 */
const getPaperType = ({ widthPx, heightPx }: { widthPx: number; heightPx: number }): PaperType | undefined => {
  for (const [type, preset] of Object.entries(PageSizePreset)) {
    if (
      [widthPx as number, heightPx as number].includes(preset.widthPx) &&
      [widthPx as number, heightPx as number].includes(preset.heightPx)
    ) {
      return type as PaperType;
    }
  }
};

/**
 * Format and convert giving pixel value to specific unit value
 *
 * @param value - Value in pixels
 * @param unit - Conversion unit
 * @returns  A value with specific unit
 */
const format = (value: string | number, unit: PageSizeUnit): string => {
  switch (unit) {
    case PageSizeUnit.px:
      return value.toString();
    case PageSizeUnit.in:
      return pixelToInch(Number(value)).toString();
    case PageSizeUnit.cm:
      return pixelToCm(Number(value)).toString();
    default:
      break;
  }

  return '';
};

/**
 * Check whether the value is whithin the min and max input range
 *
 * @param value - A numeric value
 * @returns
 */
const validateSizeInput = (value: unknown, unit: PageSizeUnit): boolean => {
  const checkValue = Number(value);
  return (
    !isNaN(checkValue) &&
    checkValue >= Number(format(MIN_NUMBER_INPUT, unit)) &&
    checkValue <= Number(format(MAX_NUMBER_INPUT, unit))
  );
};

/**
 * Determine long paper or not by aspect ratio, considered long paper if aspect ratio is greater than or equal to 2.5.
 *
 * @param pageSize Current page size.
 * @returns {boolean}
 */
const isLongPaper = (pageSize: Omit<PageSize, 'paperType'>): boolean => {
  return (
    pageSize.widthPx / pageSize.heightPx >= LONG_PAPER_RATIO || pageSize.heightPx / pageSize.widthPx >= LONG_PAPER_RATIO
  );
};

export interface PageDimensions {
  widthPx: number;
  heightPx: number;
}

export const getDimensionsForOrientation = (
  dimensions: PageDimensions,
  orientation: 'landscape' | 'portrait',
): PageDimensions => {
  const minValue = Math.min(dimensions.widthPx, dimensions.heightPx);
  const maxValue = Math.max(dimensions.widthPx, dimensions.heightPx);

  return {
    widthPx: orientation === 'portrait' ? minValue : maxValue,
    heightPx: orientation === 'portrait' ? maxValue : minValue,
  };
};

export const getCurrentOrientation = (dimensions: PageDimensions): 'landscape' | 'portrait' => {
  return dimensions.widthPx > dimensions.heightPx ? 'landscape' : 'portrait';
};

/**
 * Detect orientation change.
 *
 * @param currentSize Current page size.
 * @param previousSize Previous page size.
 * @returns {boolean}
 */
export const isOrientationChanged = (
  currentSize: Omit<PageSize, 'paperType'>,
  previousSize: Omit<PageSize, 'paperType'>,
): boolean => {
  return (
    (currentSize.widthPx <= currentSize.heightPx && !(previousSize.widthPx <= previousSize.heightPx)) ||
    (currentSize.widthPx > currentSize.heightPx && !(previousSize.widthPx > previousSize.heightPx))
  );
};

/**
 * Get paper orientation based on page size.
 *
 * @param currentSize Current page size.
 * @returns {Orientation}
 */
export const getOrientation = (currentSize: Omit<PageSize, 'paperType'>): Orientation => {
  if (currentSize.widthPx <= currentSize.heightPx) {
    return Orientation.portrait;
  }

  return Orientation.landscape;
};

export const getPageSizeExceed = (currentSize: Omit<PageSize, 'paperType'>, unit: PageSizeUnit): boolean => {
  const formattedMax: number = Number(format(MAX_NUMBER_INPUT, unit));
  const formattedMin: number = Number(format(MIN_NUMBER_INPUT, unit));

  return (
    typeof currentSize.initialSize === 'number' &&
    (Number(format(currentSize.initialSize, unit)) > formattedMax ||
      Number(format(currentSize.initialSize, unit)) < formattedMin)
  );
};

export const hasExceededHDExport = (currentSize: Omit<PageSize, 'paperType'>, unit: PageSizeUnit): boolean => {
  const breakPointValue = Number(format(HD_EXPORT_BREAKPOINT, unit));
  return (
    Number(format(currentSize.widthPx, unit)) > breakPointValue &&
    Number(format(currentSize.heightPx, unit)) > breakPointValue
  );
};

export {
  pixelToInch,
  inchToPixel,
  pixelToCm,
  cmToPixel,
  toPixels,
  getPaperType,
  format,
  validateSizeInput,
  isLongPaper,
};
